import React from "react"
import { Link } from "react-router-dom"

const CanvasTokenHelp = () => {
  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <Link
          to="/settings"
          className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-700"
        >
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Settings
        </Link>
      </div>

      <div className="bg-white rounded-xl border shadow-sm p-6 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">How to Get Your Canvas Access Token</h1>
            <p className="text-gray-500">Follow these steps to connect Heartily to your Canvas account</p>
          </div>
        </div>

        <div className="space-y-8">
          {/* Step 1 */}
          <div className="border-l-4 border-indigo-500 pl-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-indigo-600 text-white text-sm font-bold">1</span>
              <h2 className="text-lg font-semibold text-gray-900">Go to Account Settings</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Log in to Canvas and click on <strong>Account</strong> in the left sidebar, then select <strong>Settings</strong>.
            </p>
            <div className="rounded-lg overflow-hidden border shadow-sm">
              <img
                src="/images/Settingslink.png"
                alt="Canvas Account Settings menu showing the Settings link"
                className="w-full"
              />
            </div>
          </div>

          {/* Step 2 */}
          <div className="border-l-4 border-indigo-500 pl-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-indigo-600 text-white text-sm font-bold">2</span>
              <h2 className="text-lg font-semibold text-gray-900">Click "New Access Token"</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Scroll down to the <strong>Approved Integrations</strong> section and click the <strong>+ New Access Token</strong> button.
            </p>
            <div className="rounded-lg overflow-hidden border shadow-sm">
              <img
                src="/images/newtokenbutton.png"
                alt="Canvas settings page showing the New Access Token button"
                className="w-full"
              />
            </div>
          </div>

          {/* Step 3 */}
          <div className="border-l-4 border-indigo-500 pl-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-indigo-600 text-white text-sm font-bold">3</span>
              <h2 className="text-lg font-semibold text-gray-900">Create Your Token</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Enter a purpose for your token (e.g., "Heartily") and optionally set an expiration date.
              Click <strong>Generate Token</strong>.
            </p>
            <div className="rounded-lg overflow-hidden border shadow-sm">
              <img
                src="/images/createtoken.png"
                alt="Canvas dialog for creating a new access token"
                className="w-full"
              />
            </div>
          </div>

          {/* Step 4 */}
          <div className="border-l-4 border-indigo-500 pl-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-indigo-600 text-white text-sm font-bold">4</span>
              <h2 className="text-lg font-semibold text-gray-900">Copy Your Token</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Canvas will display your new access token. <strong>Copy it now</strong> — you won't be able to see it again!
              Paste this token into Heartily's settings page.
            </p>
            <div className="rounded-lg overflow-hidden border shadow-sm">
              <img
                src="/images/tokendisplay.png"
                alt="Canvas showing the generated access token"
                className="w-full"
              />
            </div>
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex gap-2">
                <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-sm text-amber-800">
                  <strong>Important:</strong> This is the only time your token will be shown. If you lose it, you'll need to generate a new one.
                </p>
              </div>
            </div>
          </div>

          {/* Finding Your Canvas URL */}
          <div className="mt-10 pt-8 border-t">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Finding Your Canvas URL</h2>
                <p className="text-gray-500">You'll also need your school's Canvas URL</p>
              </div>
            </div>

            <p className="text-gray-600 mb-4">
              Your Canvas URL is the web address you use to access Canvas. Look at your browser's address bar when you're logged into Canvas —
              it will look something like <code className="px-2 py-1 bg-gray-100 rounded text-sm">https://yourschool.instructure.com</code>.
            </p>
            <div className="rounded-lg overflow-hidden border shadow-sm">
              <img
                src="/images/instuctureurl.png"
                alt="Browser address bar showing Canvas URL"
                className="w-full"
              />
            </div>
            <p className="mt-4 text-sm text-gray-500">
              Copy this URL (without any path after the domain) and paste it into the "Instance URL" field in Heartily.
            </p>
          </div>

          {/* CTA */}
          <div className="mt-8 pt-6 border-t text-center">
            <p className="text-gray-600 mb-4">Ready to connect your Canvas account?</p>
            <Link
              to="/settings"
              className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Go to Settings
              <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CanvasTokenHelp
